import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} track - The track containing the repeated logo groups.
 *
 * @extends {Component<Refs>}
 */
class ScrollingBrandLogosCarousel extends Component {
  requiredRefs = ['track'];

  connectedCallback() {
    super.connectedCallback();
    this.#fillTrack();
    window.addEventListener('resize', this.#onResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#onResize);
  }

  #onResize = debounce(() => this.#fillTrack(), 200);

  /**
   * Liquid can't measure real pixel widths, so it only renders a rough guess at how
   * many logo groups are needed to cover the viewport twice over - the minimum for the
   * CSS marquee loop to never expose a gap of empty space at the point where it wraps
   * back to the start (which reads as the carousel "jumping"/restarting). Top that
   * guess up here with the actual rendered sizes, cloning the first group until
   * there's enough, then update the count the wrap distance is calculated from.
   */
  #fillTrack = () => {
    // With reduced motion, the CSS hides every duplicated group and the track never
    // animates, so there's nothing here for extra clones to fix.
    if (prefersReducedMotion()) return;

    const { track } = this.refs;
    const groups = Array.from(track.querySelectorAll('.scrolling-brand-logos__group'));
    const firstGroup = groups[0];

    if (!firstGroup) return;

    const targetWidth = this.offsetWidth * 2;
    let groupCount = groups.length;
    let addedGroups = 0;

    while (track.scrollWidth < targetWidth && groupCount < 40) {
      const clone = /** @type {HTMLElement} */ (firstGroup.cloneNode(true));
      clone.setAttribute('aria-hidden', 'true');
      track.appendChild(clone);
      groupCount += 1;
      addedGroups += 1;
    }

    if (addedGroups === 0) return;

    track.style.setProperty('--scrolling-brand-logos-groups', String(groupCount));

    // Changing the group count changes the distance the wrap-around jump travels, so the
    // running animation must be restarted against the new value - otherwise browsers keep
    // using the count it started with until the next natural iteration, and the newly
    // added groups render but scroll off screen before ever being used.
    track.style.animation = 'none';
    void track.offsetWidth;
    track.style.animation = '';
  };
}

if (!customElements.get('scrolling-brand-logos-carousel')) {
  customElements.define('scrolling-brand-logos-carousel', ScrollingBrandLogosCarousel);
}
